// src/components/admin/page-sections/ImageSection.js
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const ImageSection = ({ content }) => {
  const { 
    imageUrl, 
    imageAlt = '',
    caption = '',
    alignment = 'center',
    size = 'medium',
    width = '100%',
    maxHeight = 'none',
    aspectRatio = 'auto',
    objectFit = 'cover',
    effect = 'none',
    animation = 'none',
    shadow = '',
    borderRadius = '0',
    borderStyle = 'none',
    borderWidth = '1px',
    borderColor = '#000',
    linkUrl = '',
    overlay = null,
    aspectRatioWidth = '16',
    aspectRatioHeight = '9'
  } = content || {};

  const imageRef = useRef(null);

  // Define CSS keyframes for animations - this effect must be called unconditionally
  useEffect(() => {
    // Only add if they don't already exist
    if (!document.querySelector('#image-section-animations')) {
      const styleElement = document.createElement('style');
      styleElement.id = 'image-section-animations';
      styleElement.innerHTML = `
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes slideInLeft {
          from { transform: translateX(-50px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideInRight {
          from { transform: translateX(50px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes zoomIn {
          from { transform: scale(0.8); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-fadeIn { animation: fadeIn 1s ease forwards; }
        .animate-slideUp { animation: slideUp 1s ease forwards; }
        .animate-slideInLeft { animation: slideInLeft 1s ease forwards; }
        .animate-slideInRight { animation: slideInRight 1s ease forwards; }
        .animate-zoomIn { animation: zoomIn 1s ease forwards; }
      `;
      document.head.appendChild(styleElement);
    }
  }, []);

  // Handle animations when the element comes into view
  useEffect(() => {
    if (animation && animation !== 'none' && imageRef.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              // Add animation class when element is in view
              entry.target.style.opacity = '1';
              entry.target.classList.add(`animate-${animation}`);
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1 }
      );

      // Set initial opacity
      imageRef.current.style.opacity = '0';
      observer.observe(imageRef.current);

      return () => {
        if (imageRef.current) {
          observer.unobserve(imageRef.current);
        }
      };
    }
  }, [animation, imageUrl]);

  // Early return if no image URL - AFTER all hooks are called
  if (!imageUrl) {
    return null;
  }

  // Calculate container classes based on alignment
  const containerClasses = [
    alignment === 'left' ? 'mr-auto' : 
    alignment === 'right' ? 'ml-auto' : 'mx-auto',
    shadow
  ].filter(Boolean).join(' ');

  // Calculate actual aspect ratio if set
  let aspectRatioStyle = {};
  if (aspectRatio && aspectRatio !== 'auto') {
    if (aspectRatio === 'custom' && aspectRatioWidth && aspectRatioHeight) {
      aspectRatioStyle.aspectRatio = `${aspectRatioWidth}/${aspectRatioHeight}`;
    } else {
      aspectRatioStyle.aspectRatio = aspectRatio;
    }
  }

  // Calculate width based on size
  const widthStyle = width || (
    size === 'small' ? '300px' : 
    size === 'medium' ? '500px' : 
    size === 'large' ? '800px' : 
    size === 'full' ? '100%' : '500px'
  );

  // Create border style when needed
  let borderStyleObj = {};
  if (borderStyle && borderStyle !== 'none') {
    borderStyleObj = {
      borderWidth: borderWidth || '1px',
      borderStyle: borderStyle,
      borderColor: borderColor || '#000'
    };
  }

  // Map animation types to CSS animation classes
  const getAnimationClass = () => {
    switch(animation) {
      case 'fadeIn': return 'animate-fadeIn';
      case 'slideUp': return 'animate-slideUp';
      case 'slideInLeft': return 'animate-slideInLeft';
      case 'slideInRight': return 'animate-slideInRight';
      case 'zoomIn': return 'animate-zoomIn';
      case 'pulse': return 'animate-pulse';
      default: return '';
    }
  };

  // Render the final image with all applied styles and effects
  const renderImage = () => {
    const imageContent = (
      <div 
        ref={imageRef}
        className={`relative overflow-hidden transition-all duration-300 ${containerClasses} ${getAnimationClass()}`}
        style={{
          width: widthStyle,
          maxHeight: maxHeight,
          borderRadius: borderRadius,
          ...aspectRatioStyle,
          ...borderStyleObj
        }}>
        <img 
          src={imageUrl}
          alt={imageAlt} 
          className="w-full h-full"
          style={{
            objectFit: objectFit,
            filter: effect !== 'none' ? effect : undefined,
          }}
        />
        
        {overlay && overlay.enabled && (
          <div 
            className="absolute inset-0"
            style={{
              backgroundColor: overlay.color || 'rgba(0,0,0,0.4)',
              mixBlendMode: overlay.blendMode || 'normal'
            }}
          ></div>
        )}
      </div>
    );
    
    // Return with or without link wrapping
    // Check if URL is external (starts with http or https)
    if (linkUrl) {
      if (linkUrl.startsWith('http://') || linkUrl.startsWith('https://')) {
        return (
          <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="block" style={{ width: widthStyle }}>
            {imageContent}
          </a>
        );
      } else {
        return (
          <Link to={linkUrl} className="block" style={{ width: widthStyle }}>
            {imageContent}
          </Link>
        );
      }
    }
    
    return imageContent;
  };

  return (
    <div className="py-8">
      {renderImage()}
      
      {caption && (
        <div className={`mt-3 text-gray-600 text-sm ${alignment === 'left' ? 'text-left' : alignment === 'right' ? 'text-right' : 'text-center'}`} style={{ width: widthStyle, margin: alignment === 'left' ? '0' : alignment === 'right' ? '0 0 0 auto' : '0 auto' }}>
          {caption}
        </div>
      )}

      <style>{`
        /* Base animations for image sections */
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        @keyframes slideInLeft {
          from { transform: translateX(-50px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        @keyframes slideInRight {
          from { transform: translateX(50px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        @keyframes zoomIn {
          from { transform: scale(0.8); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }

        /* Animation classes to apply directly to elements */
        .animate-fadeIn { animation: fadeIn 1s ease forwards; }
        .animate-slideUp { animation: slideUp 1s ease forwards; }
        .animate-slideInLeft { animation: slideInLeft 1s ease forwards; }
        .animate-slideInRight { animation: slideInRight 1s ease forwards; }
        .animate-zoomIn { animation: zoomIn 1s ease forwards; }
        .animate-pulse { animation: pulse 2s infinite; }

        /* Hover effect classes */
        .hover-zoom {
          transition: transform 0.3s ease;
        }

        .hover-zoom:hover {
          transform: scale(1.05);
        }

        .hover-brightness {
          transition: filter 0.3s ease;
        }

        .hover-brightness:hover {
          filter: brightness(1.1);
        }

        .hover-shadow {
          transition: box-shadow 0.3s ease;
        }

        .hover-shadow:hover {
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
        }

        /* Image container styles */
        .image-container {
          position: relative;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        /* Caption styles */
        .image-caption {
          transition: opacity 0.3s ease;
          padding: 8px 0;
          font-size: 0.875rem;
          color: #666;
        }

        /* Overlay hover effects */
        .overlay-hover {
          position: relative;
        }

        .overlay-hover::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.3);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .overlay-hover:hover::after {
          opacity: 1;
        }

        /* Image filters */
        .filter-grayscale {
          filter: grayscale(100%);
          transition: filter 0.3s ease;
        }

        .filter-grayscale:hover {
          filter: grayscale(0%);
        }

        .filter-sepia {
          filter: sepia(70%);
          transition: filter 0.3s ease;
        }

        .filter-sepia:hover {
          filter: sepia(0%);
        }

        /* Aspect ratio containers */
        .aspect-16-9 {
          aspect-ratio: 16/9;
        }

        .aspect-4-3 {
          aspect-ratio: 4/3;
        }

        .aspect-1-1 {
          aspect-ratio: 1/1;
        }

        .aspect-3-4 {
          aspect-ratio: 3/4;
        }

        .aspect-9-16 {
          aspect-ratio: 9/16;
        }

        /* Object fit options */
        .object-fit-cover {
          object-fit: cover;
          width: 100%;
          height: 100%;
        }

        .object-fit-contain {
          object-fit: contain;
          width: 100%;
          height: 100%;
        }

        .object-fit-fill {
          object-fit: fill;
          width: 100%;
          height: 100%;
        }

        /* Border radius presets */
        .rounded-sm { border-radius: 4px; }
        .rounded-md { border-radius: 8px; }
        .rounded-lg { border-radius: 16px; }
        .rounded-full { border-radius: 9999px; }

        /* Animation delay utilities */
        .delay-100 { animation-delay: 100ms; }
        .delay-300 { animation-delay: 300ms; }
        .delay-500 { animation-delay: 500ms; }
        .delay-700 { animation-delay: 700ms; }
        .delay-1000 { animation-delay: 1000ms; }
      `}
      </style>
    </div>

  );
};

export default ImageSection;
